import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';

/**
 * LevelUpModal
 * 
 * 레벨업 시 발생하는 프리미엄 축하 모달
 * - Glassmorphism 디자인
 * - Neon Glow 효과
 * - Canvas Confetti 폭죽 효과
 */
export default function LevelUpModal({ show, level, onClose }) {

    // Confetti Effect
    useEffect(() => {
        if (show) {
            const duration = 3 * 1000;
            const animationEnd = Date.now() + duration;
            const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

            const randomInRange = (min, max) => Math.random() * (max - min) + min;

            const interval = setInterval(function () {
                const timeLeft = animationEnd - Date.now();

                if (timeLeft <= 0) {
                    return clearInterval(interval);
                }

                const particleCount = 50 * (timeLeft / duration);
                confetti({
                    ...defaults,
                    particleCount,
                    origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
                });
                confetti({
                    ...defaults,
                    particleCount,
                    origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
                });
            }, 250);

            return () => clearInterval(interval);
        }
    }, [show]);

    // Auto Close Timer
    useEffect(() => {
        if (show && onClose) {
            const timer = setTimeout(() => {
                onClose();
            }, 4000); // 4초 후 자동 닫힘
            return () => clearTimeout(timer);
        }
    }, [show, onClose]);

    if (!show) return null;

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.5, y: 100, opacity: 0 }}
                        animate={{
                            scale: [0.5, 1.1, 1],
                            y: 0,
                            opacity: 1,
                            rotate: [0, -2, 2, 0]
                        }}
                        exit={{ scale: 0.8, opacity: 0, y: -50 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        className="relative p-10 rounded-3xl bg-gradient-to-br from-slate-900/90 to-indigo-950/90 border border-indigo-400/50 shadow-[0_0_100px_rgba(99,102,241,0.5)] text-center max-w-md w-full overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Background Effects */}
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.2)_0%,transparent_70%)] animate-pulse"></div>
                        <div className="absolute -top-24 -left-24 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl"></div>
                        <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-cyan-500/20 rounded-full blur-3xl"></div>

                        {/* Level Badge Badge */}
                        <motion.div
                            className="relative w-32 h-32 mx-auto mb-6 flex items-center justify-center"
                            initial={{ rotateY: 90 }}
                            animate={{ rotateY: 0 }}
                            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-tr from-yellow-400 to-orange-500 rounded-full blur-lg opacity-60 animate-pulse"></div>
                            <div className="relative w-full h-full bg-gradient-to-br from-yellow-300 via-orange-400 to-yellow-600 rounded-full flex items-center justify-center shadow-[inset_0_2px_10px_rgba(255,255,255,0.4),0_10px_30px_rgba(234,179,8,0.5)] border-4 border-yellow-200">
                                <i className="fas fa-crown text-5xl text-white drop-shadow-md transform -translate-y-1"></i>
                            </div>

                            {/* Floating Stars */}
                            <motion.i
                                className="fas fa-star text-yellow-300 absolute top-0 right-0 text-2xl drop-shadow-[0_0_10px_rgba(253,224,71,0.8)]"
                                animate={{ y: [0, -10, 0], scale: [1, 1.2, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity, delay: 0.1 }}
                            />
                            <motion.i
                                className="fas fa-star text-yellow-200 absolute bottom-2 left-0 text-xl drop-shadow-[0_0_10px_rgba(253,224,71,0.8)]"
                                animate={{ y: [0, -8, 0], scale: [1, 1.1, 1] }}
                                transition={{ duration: 1.8, repeat: Infinity, delay: 0.3 }}
                            />
                        </motion.div>

                        {/* Text Content */}
                        <motion.h2
                            className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-white to-yellow-300 mb-2 drop-shadow-[0_2px_10px_rgba(234,179,8,0.5)]"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 }}
                        >
                            LEVEL UP!
                        </motion.h2>

                        <motion.div
                            className="text-xl text-indigo-200 font-medium tracking-wide mb-6"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            이제 <span className="text-yellow-400 font-bold text-2xl mx-1">Lv.{level}</span> 개발자입니다!
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8 }}
                        >
                            <button
                                onClick={onClose}
                                className="px-8 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold shadow-[0_4px_20px_rgba(79,70,229,0.4)] transition-all hover:scale-105 active:scale-95 border border-white/10"
                            >
                                멋져요! 계속하기 🚀
                            </button>
                        </motion.div>

                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
